import { ZigButton, ZigButtonProps } from '@zignaly-open/ui';
import { OpenInNew } from '@mui/icons-material';
import { downloadFile, useCurrentlyManagedWl } from './Config/use';
import React from 'react';
import { Tooltip, useTheme, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWlConfigQuery } from '../apis/config/api';

type UseLazyExportCsvQueryType<T> = () => readonly [
  (args: T) => Promise<{ data?: string }>,
  { isFetching: boolean },
  ...unknown[],
];

export default function ExportButton<T>({
  useLazyExportCsvQuery,
  filters,
  fileName = 'download',
  ...props
}: {
  useLazyExportCsvQuery: UseLazyExportCsvQueryType<T>;
  filters: T;
  fileName?: string;
} & ZigButtonProps) {
  const theme = useTheme();
  const { t } = useTranslation(['common']);

  const wl = useCurrentlyManagedWl();
  const { data } = useWlConfigQuery(wl);

  const [exportCsv, { isFetching }] = useLazyExportCsvQuery();

  return (
    <Tooltip
      title={
        isFetching
          ? 'The export is in progress. The download will start soon. Please wait'
          : ''
      }
    >
      <Box display={'flex'} sx={{ maxWidth: '90px' }}>
        <ZigButton
          {...props}
          variant={'text'}
          sx={{
            ...props.sx,
            mt: '25px',

            '.MuiSvgIcon-root.MuiSvgIcon-root': {
              fill: theme.palette.links,
            },
            '.MuiLoadingButton-loadingIndicatorCenter': {
              transform: 'translate(-50%, 10%)',
            },
          }}
          endIcon={
            !isFetching && (
              <OpenInNew sx={{ width: '17.33px', height: '17.33px' }} />
            )
          }
          onClick={async () => {
            try {
              const result = await exportCsv(filters);
              if (result.data) {
                const file = new File(
                  [result.data],
                 `${fileName} ${data.name}`,
                  {
                    type: 'text/csv;charset=utf-8;',
                  },
                );
                downloadFile(file);
              }
            } catch (e) {
              console.error(e);
            }
          }}
          loading={isFetching}
        >
          {Object.entries(filters).filter(
            ([key, value]) =>
              key !== 'operator' && value !== '' && value !== '0',
          ).length
            ? t('common:export')
            : t('common:export-all')}
        </ZigButton>
      </Box>
    </Tooltip>
  );
}
